import type { CompoundEntityRef } from '@backstage/catalog-model';
import { createApiRef } from '@backstage/core-plugin-api';

function entityKey(entityId: CompoundEntityRef): string {
  return `${entityId.kind}:${entityId.namespace}:${entityId.name}`.toLowerCase();
}

/**
 * Tracks which branch/tag is currently selected for each entity's docs. The
 * version picker writes to it; the RdapTechDocsStorageApi reads it on every
 * call to decide whether to serve the stock (registered-branch) build or a
 * versioned one, and the reader page subscribes to know when to remount.
 */
export class TechDocsRdapVersionStore {
  private readonly selections = new Map<string, string>();
  private readonly listeners = new Set<() => void>();

  /** The selected ref for an entity, or undefined if using the registered default. */
  get(entityId: CompoundEntityRef): string | undefined {
    return this.selections.get(entityKey(entityId));
  }

  set(entityId: CompoundEntityRef, ref: string | undefined): void {
    const key = entityKey(entityId);
    if (ref === undefined) {
      this.selections.delete(key);
    } else {
      this.selections.set(key, ref);
    }
    this.listeners.forEach(listener => listener());
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

export const techdocsRdapVersionStoreApiRef = createApiRef<TechDocsRdapVersionStore>({
  id: 'rdap.techdocs.version-store',
});
